import { useState, useEffect, useImperativeHandle, forwardRef, useRef, useCallback } from "react";
import { motion } from "framer-motion";

interface CountdownTimerProps {
  totalSeconds: number;
  onComplete: () => void;
  className?: string;
  startPaused?: boolean;
}

export interface CountdownTimerHandle {
  pause: () => void;
  resume: () => void;
}

const CountdownTimer = forwardRef<CountdownTimerHandle, CountdownTimerProps>(
  ({ totalSeconds, onComplete, className = "", startPaused = false }, ref) => {
    // Deadline-based: immune to tab backgrounding
    const [deadline, setDeadline] = useState<number | null>(
      startPaused ? null : Date.now() + totalSeconds * 1000
    );
    const [remaining, setRemaining] = useState(totalSeconds);
    const completedRef = useRef(false);
    const onCompleteRef = useRef(onComplete);
    onCompleteRef.current = onComplete;

    useImperativeHandle(ref, () => ({
      pause: () => {
        // Only used for tutorial delay — captures remaining time then clears deadline
        setDeadline((prev) => {
          if (prev) {
            const left = Math.max(0, Math.ceil((prev - Date.now()) / 1000));
            setRemaining(left);
          }
          return null;
        });
      },
      resume: () => {
        setDeadline(Date.now() + remaining * 1000);
      },
    }));

    useEffect(() => {
      if (!deadline || completedRef.current) return;

      const tick = () => {
        const now = Date.now();
        const left = Math.max(0, Math.ceil((deadline - now) / 1000));
        setRemaining(left);

        if (left <= 0 && !completedRef.current) {
          completedRef.current = true;
          onCompleteRef.current();
        }
      };

      tick(); // immediate sync
      const interval = setInterval(tick, 250); // frequent ticks to catch tab-resume instantly
      return () => clearInterval(interval);
    }, [deadline]);

    const minutes = Math.floor(remaining / 60);
    const seconds = remaining % 60;
    const progress = remaining / totalSeconds;

    // Color transitions: green > orange > red
    const timerColor = progress > 0.5
      ? "hsl(142, 71%, 45%)"
      : progress > 0.2
        ? "hsl(32, 95%, 50%)"
        : "hsl(0, 84%, 50%)";

    const textColorClass = progress > 0.5
      ? "text-green-500"
      : progress > 0.2
        ? "text-orange-500"
        : "text-red-500 animate-countdown-pulse";

    return (
      <div className={`flex items-center gap-3 ${className}`}>
        <div className="relative w-12 h-12">
          <svg className="w-full h-full -rotate-90" viewBox="0 0 36 36">
            <circle cx="18" cy="18" r="15.5" fill="none" stroke="hsl(var(--border))" strokeWidth="2" />
            <motion.circle
              cx="18" cy="18" r="15.5" fill="none"
              stroke={timerColor} strokeWidth="2.5" strokeLinecap="round"
              strokeDasharray={97.4} strokeDashoffset={97.4 * (1 - progress)}
              transition={{ duration: 0.5 }}
            />
          </svg>
          {/* No pause button — timer always runs */}
          <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
            <span className={`text-[10px] font-bold ${textColorClass}`}>⏱</span>
          </div>
        </div>
        <span className={`font-display text-lg font-bold tabular-nums ${textColorClass}`}>
          {minutes}:{seconds.toString().padStart(2, "0")}
        </span>
      </div>
    );
  }
);

CountdownTimer.displayName = "CountdownTimer";
export default CountdownTimer;
